import _ from 'lodash';
import React, {useCallback, useMemo} from 'react';

import {Analytics} from '../../../../../services/analytics';
import {useMemoizedSelector} from '../../../../../state/hooks';
import {useRunsData, useRunSetsQuery} from '../../../../../state/runs/hooks';
import type * as PanelTypes from '../../../../../state/views/panel/types';
import {type Ref as SectionRef} from '../../../../../state/views/panelBankSectionConfig/types';
import {panelViews} from '../../../../PanelBank/types';
import {
  type MediaBrowserPanelConfig,
  Spec,
} from '../../../../PanelMediaBrowser';
import {useBoundingBoxes} from '../../../../PanelMediaBrowser/hooks/useBoundingBoxes';
import {useSliderRanges} from '../../../../PanelMediaBrowser/hooks/useSliderRanges';
import {useMediaCardTypes} from '../../../../PanelMediaBrowser/useGetMediaCardTypes';
import {useConfigWithDefaults} from '../../../../PanelMediaBrowser/useMediaPanelValues';
import {usePanelsSharedRefsContext} from '../../../../Views/contexts/PanelsSharedRefsContext';
import {useMaybeWorkspaceRefsContext} from '../../../../Workspace/WorkspaceRefsContext';
import {usePanelSettingsConfigUpdates} from '../../hooks/usePanelSettingsConfigUpdates';
import {
  makeCascadingPanelMediaConfigSelector,
  selectCustomRunColors,
} from '../mediaSettingsSelectors';
import {type ContextProviderState, SettingsLevel} from '../types';
import {MediaSettingsContextProvider} from './MediaSettingsContextProvider';
import {useRampFlagsForMediaSettings} from './utils';

export type MediaPanelSettingsProps = {
  panelRef: PanelTypes.Ref;
  panelBankSectionConfigRef: SectionRef;
};

/**
 * Provider for panel-level media settings.
 *
 * This provider wraps MediaSettingsContextProvider and internally uses
 * usePanelMediaSettingsDrawer to compute the context value.
 *
 * Usage:
 * ```tsx
 * <MediaPanelSettingsContextProvider panelRef={panelRef} panelBankSectionConfigRef={sectionRef}>
 *   <MediaSettingsDrawerContent ... />
 * </MediaPanelSettingsContextProvider>
 * ```
 */
export function MediaPanelSettingsContextProvider({
  children,
  panelRef,
  panelBankSectionConfigRef,
}: MediaPanelSettingsProps & {children: React.ReactNode}) {
  const contextValue = usePanelMediaSettingsDrawer({
    panelRef,
    panelBankSectionConfigRef,
  });
  return (
    <MediaSettingsContextProvider contextValue={contextValue}>
      {children}
    </MediaSettingsContextProvider>
  );
}

/**
 * Internal hook - do not use directly.
 * Use MediaPanelSettingsContextProvider instead and use useMediaSettingsContext, useMediaSettingsActions, or useMediaSettingsState to access the context value(s).
 */
const usePanelMediaSettingsDrawer = ({
  panelRef,
  panelBankSectionConfigRef,
}: MediaPanelSettingsProps): ContextProviderState => {
  const {config, viewType, updateConfig, unsetConfigFields} =
    usePanelSettingsConfigUpdates<MediaBrowserPanelConfig, 'Media Browser'>({
      panelRef,
      panelBankSectionConfigRef,
    });

  // This drawer is only supported for a single media panel which only allows a single media type.
  const mediaCardType = useMediaCardTypes(
    viewType === panelViews.mediaBrowser
      ? (config as MediaBrowserPanelConfig)?.mediaKeys
      : undefined
  )?.[0];
  const {customRunColorsRef, runSetRefs, customRunNamesRef} =
    usePanelsSharedRefsContext();

  const customRunColors = useMemoizedSelector(
    selectCustomRunColors,
    customRunColorsRef
  );
  const query = useRunSetsQuery({runSetRefs, customRunNamesRef});

  const panelConfig = config;
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const cascadedConfig = useMemoizedSelector(
    makeCascadingPanelMediaConfigSelector,
    workspaceRefs?.workspaceSettingsRef,
    panelBankSectionConfigRef,
    panelRef
  );

  // We need to load data about the current media shown in the panel so that we can know what to default
  // certain settings to.
  const panelQuery = useMemo(
    () => Spec.transformQuery(query, panelConfig),
    [query, panelConfig]
  );
  // loading these defaults shouldn't block the settings drawer
  const {data, loading} = useRunsData(panelQuery);
  const {configWithDefaults, hasMasks, allBoxes, allMasks} =
    useConfigWithDefaults({
      data,
      query: panelQuery,
      config: cascadedConfig,
      customRunColors,
      pageQuery: query,
    });

  const updateConfigAndLog = useCallback(
    (configUpdate: Partial<MediaBrowserPanelConfig>) => {
      updateConfig(configUpdate);
      const updatedKeys = Object.keys(
        configUpdate
      ) as (keyof MediaBrowserPanelConfig)[];
      const preConfigUpdates = _.pick(configWithDefaults, updatedKeys);
      const eventData = {
        configUpdate: JSON.stringify(configUpdate),
        preConfigUpdate: JSON.stringify(preConfigUpdates),
        mediaType: mediaCardType,
        mode: configUpdate.mode ?? configWithDefaults.mode,
      };
      Analytics.track('Update Panel Media Settings', eventData);
    },
    [updateConfig, configWithDefaults, mediaCardType]
  );

  const unsetConfigFieldsAndLog = useCallback(
    (fields: Array<keyof MediaBrowserPanelConfig>) => {
      unsetConfigFields(fields);
      // `field` keeps the historical string shape for existing queries;
      // `fields` carries the structured list.
      Analytics.track('Unset Panel Media Setting', {
        field: fields.join(','),
        fields,
      });
    },
    [unsetConfigFields]
  );

  const unsetConfigFieldAndLog = useCallback(
    (field: keyof MediaBrowserPanelConfig) => unsetConfigFieldsAndLog([field]),
    [unsetConfigFieldsAndLog]
  );

  const boundingBoxes = useBoundingBoxes(
    allBoxes,
    panelConfig.mediaKeys,
    panelRef.id
  );
  const sliderRanges = useSliderRanges(panelConfig.mediaKeys, panelRef.id);
  const rampFlags = useRampFlagsForMediaSettings();

  return useMemo(
    () => ({
      config: configWithDefaults,
      configWithoutDefaults: panelConfig,
      hasMasks,
      hasBoundingBoxes: Object.keys(boundingBoxes).length > 0,
      loading,
      mediaCardTypes: mediaCardType ? [mediaCardType] : undefined,
      rampFlags,
      updateConfig: updateConfigAndLog,
      unsetConfigField: unsetConfigFieldAndLog,
      unsetConfigFields: unsetConfigFieldsAndLog,
      allBoxes: boundingBoxes,
      sliderRanges,
      allMasks,
      settingsLevel: SettingsLevel.PANEL,
      panelRef,
    }),
    [
      configWithDefaults,
      panelConfig,
      panelRef,
      hasMasks,
      boundingBoxes,
      loading,
      mediaCardType,
      rampFlags,
      updateConfigAndLog,
      unsetConfigFieldAndLog,
      unsetConfigFieldsAndLog,
      sliderRanges,
      allMasks,
    ]
  );
};
